import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Globe, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useCountry } from "../lib/country";
import { toast } from "sonner";

/**
 * Shown once per newly registered user. Asks them to set their default country
 * (SV or US). Saves to user.country + flag country_chosen=true so it never
 * appears again. User can still change country anytime from the navbar selector.
 */
export default function CountryPicker() {
    const { user, refresh } = useAuth();
    const { setCountry } = useCountry();
    const [saving, setSaving] = useState(false);
    const [accepted, setAccepted] = useState(false);

    if (!user) return null;
    if (user.country_chosen) return null;
    if (user.role === "admin") return null; // admins are usually power users; skip prompt

    const pick = async (c) => {
        if (!accepted) {
            toast.error("Debes aceptar los Términos y Condiciones para continuar");
            return;
        }
        setSaving(true);
        try {
            await setCountry(c, { accepted_terms: true });
            try { await refresh?.(); } catch { /* ignore */ }
            toast.success(c === "SV" ? "🇸🇻 País establecido: El Salvador" : "🇺🇸 Default country: United States");
        } catch {
            toast.error("No se pudo guardar tu país. Intenta de nuevo.");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[99998] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="country-picker-modal">
            <div className="bg-[var(--tb-bg)] border border-[var(--tb-border-gold)]/40 rounded-lg max-w-lg w-full p-6 sm:p-8 shadow-2xl">
                <div className="text-center mb-6">
                    <div className="w-14 h-14 mx-auto rounded-full bg-[var(--tb-border-gold)]/15 border border-[var(--tb-border-gold)]/40 flex items-center justify-center mb-3">
                        <Globe className="w-7 h-7 text-[var(--tb-gold)]" />
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl mb-2">¡Bienvenido a Terra Boutique!</h2>
                    <p className="text-sm text-[var(--tb-text-2)]">¿Desde qué país comprarás habitualmente? Esto evita que veas productos no disponibles en tu zona.</p>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <button onClick={() => pick("SV")} disabled={saving || !accepted}
                        className="tb-card p-5 text-center border-2 border-[var(--tb-border)] hover:border-[var(--tb-gold)] hover:bg-[var(--tb-border-gold)]/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="country-picker-sv">
                        <div className="text-4xl mb-2">🇸🇻</div>
                        <div className="font-display text-lg">El Salvador</div>
                        <div className="text-[10px] text-[var(--tb-muted)] mt-1">USD · Envíos C807 · Apartado</div>
                    </button>
                    <button onClick={() => pick("US")} disabled={saving || !accepted}
                        className="tb-card p-5 text-center border-2 border-[var(--tb-border)] hover:border-[var(--tb-gold)] hover:bg-[var(--tb-border-gold)]/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="country-picker-us">
                        <div className="text-4xl mb-2">🇺🇸</div>
                        <div className="font-display text-lg">United States</div>
                        <div className="text-[10px] text-[var(--tb-muted)] mt-1">USD · USPS Ground</div>
                    </button>
                </div>
                <label className="flex items-start gap-2 cursor-pointer text-xs text-[var(--tb-text-2)] mb-3 p-2.5 rounded border border-[var(--tb-border)] bg-[var(--tb-bg-2)]/40" data-testid="picker-terms-label">
                    <input type="checkbox" checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        className="mt-0.5 accent-[var(--tb-gold)] flex-shrink-0"
                        data-testid="picker-terms-check" />
                    <span>
                        He leído y acepto los <Link to="/legal/terminos" target="_blank" className="text-[var(--tb-gold)] hover:underline">Términos y Condiciones</Link>, la <Link to="/legal/privacidad" target="_blank" className="text-[var(--tb-gold)] hover:underline">Política de Privacidad</Link> y reconozco que muchas imágenes son generadas con IA con fines ilustrativos.
                    </span>
                </label>
                <div className="text-[10px] text-[var(--tb-muted)] flex items-start gap-1.5 bg-[var(--tb-bg-2)]/40 p-2.5 rounded border border-[var(--tb-border)]">
                    <ShieldCheck className="w-3.5 h-3.5 text-[var(--tb-gold)] mt-0.5 flex-shrink-0" />
                    <span>Podrás cambiarlo en cualquier momento desde el selector de país en la barra superior.</span>
                </div>
            </div>
        </div>
    );
}
